import type { CompendiumDetail, CompendiumType } from '../api/client';

/** Reads a string property from a loose SRD object. */
function str(obj: CompendiumDetail, key: string): string | undefined {
  const v = obj[key];
  return typeof v === 'string' ? v : undefined;
}

function num(obj: CompendiumDetail, key: string): number | undefined {
  const v = obj[key];
  return typeof v === 'number' ? v : undefined;
}

/** Joins SRD desc arrays / strings into a single markdown string. */
function descToMarkdown(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => (typeof v === 'string' ? v : ''))
      .filter(Boolean);
    return parts.length ? parts.join('\n\n') : undefined;
  }
  return undefined;
}

/** "30 ft., volare 60 ft." from the SRD speed object. */
function formatSpeed(speed: unknown): string | undefined {
  if (!speed || typeof speed !== 'object') return undefined;
  const labels: Record<string, string> = {
    walk: '',
    fly: 'volare',
    swim: 'nuotare',
    climb: 'scalare',
    burrow: 'scavare',
    hover: 'fluttuare',
  };
  const parts: string[] = [];
  for (const [key, val] of Object.entries(speed as Record<string, unknown>)) {
    const label = labels[key] ?? key;
    const text = typeof val === 'boolean' ? (val ? 'sì' : '') : String(val);
    if (!text) continue;
    parts.push(label ? `${label} ${text}` : text);
  }
  return parts.length ? parts.join(', ') : undefined;
}

function formatArmorClass(ac: unknown): string | number | undefined {
  if (typeof ac === 'number') return ac;
  if (Array.isArray(ac) && ac.length) {
    const first = ac[0] as Record<string, unknown>;
    const value = first?.value;
    const type = typeof first?.type === 'string' ? first.type : undefined;
    if (typeof value === 'number') return type ? `${value} (${type})` : value;
  }
  return undefined;
}

function formatSenses(senses: unknown): string | undefined {
  if (typeof senses === 'string') return senses;
  if (senses && typeof senses === 'object') {
    return Object.entries(senses as Record<string, unknown>)
      .map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`)
      .join(', ');
  }
  return undefined;
}

function formatList(value: unknown): string | undefined {
  if (typeof value === 'string') return value || undefined;
  if (Array.isArray(value)) {
    const parts = value.map((v) => {
      if (typeof v === 'string') return v;
      if (v && typeof v === 'object' && typeof (v as Record<string, unknown>).name === 'string') {
        return String((v as Record<string, unknown>).name);
      }
      return '';
    });
    const joined = parts.filter(Boolean).join(', ');
    return joined || undefined;
  }
  return undefined;
}

interface SrdActionLike {
  name?: unknown;
  desc?: unknown;
}

function mapActions(value: unknown): { name: string; description: string }[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((raw) => {
      const a = raw as SrdActionLike;
      const name = typeof a.name === 'string' ? a.name : '';
      const description = typeof a.desc === 'string' ? a.desc : '';
      return { name, description };
    })
    .filter((a) => a.name || a.description);
}

/** Maps a SRD monster detail object to our in-app Monster shape. */
export function srdMonsterToLocal(d: CompendiumDetail): Record<string, unknown> {
  const cr = num(d, 'challenge_rating');
  const size = str(d, 'size');
  const type = str(d, 'type');
  const alignment = str(d, 'alignment');
  const subline = [size, type, alignment].filter(Boolean).join(', ');

  const actions = mapActions(d.actions);
  const specials = mapActions(d.special_abilities);
  const legendary = mapActions(d.legendary_actions);
  const reactions = mapActions(d.reactions);

  const notesParts: string[] = [];
  if (specials.length)
    notesParts.push(
      '**Tratti**\n\n' + specials.map((a) => `**${a.name}.** ${a.description}`).join('\n\n'),
    );
  if (reactions.length)
    notesParts.push(
      '**Reazioni**\n\n' + reactions.map((a) => `**${a.name}.** ${a.description}`).join('\n\n'),
    );
  if (legendary.length)
    notesParts.push(
      '**Azioni leggendarie**\n\n' +
        legendary.map((a) => `**${a.name}.** ${a.description}`).join('\n\n'),
    );

  return {
    name: str(d, 'name') ?? d.index,
    icon: '🐉',
    type: subline || type,
    size,
    cr: cr != null ? String(cr) : undefined,
    ac: formatArmorClass(d.armor_class),
    hp: num(d, 'hit_points'),
    speed: formatSpeed(d.speed),
    stats: {
      for: num(d, 'strength'),
      des: num(d, 'dexterity'),
      cos: num(d, 'constitution'),
      int: num(d, 'intelligence'),
      sag: num(d, 'wisdom'),
      car: num(d, 'charisma'),
    },
    senses: formatSenses(d.senses),
    languages: str(d, 'languages'),
    damageResistances: formatList(d.damage_resistances),
    damageImmunities: formatList(d.damage_immunities),
    conditionImmunities: formatList(d.condition_immunities),
    actions: actions.map((a) => ({ name: a.name, description: a.description })),
    notes: notesParts.join('\n\n') || undefined,
    srcRef: `compendium:${d.index}`,
  };
}

/** Maps a SRD equipment / magic-item detail object to our in-app Item shape. */
export function srdItemToLocal(d: CompendiumDetail): Record<string, unknown> {
  const category =
    (d.equipment_category as Record<string, unknown> | undefined)?.name ??
    str(d, 'rarity') ??
    'Oggetto';
  const rarity = (d.rarity as Record<string, unknown> | undefined)?.name ?? str(d, 'rarity');
  const description = descToMarkdown(d.desc);

  const cost = d.cost as Record<string, unknown> | undefined;
  const costLine =
    cost && typeof cost.quantity === 'number' ? `Costo: ${cost.quantity} ${cost.unit}` : undefined;

  return {
    name: str(d, 'name') ?? d.index,
    icon: '✨',
    type: typeof category === 'string' ? category : 'Oggetto',
    rarity: typeof rarity === 'string' ? rarity : undefined,
    description: [description, costLine].filter(Boolean).join('\n\n') || undefined,
    srcRef: `compendium:${d.index}`,
  };
}

/** The adventure bucket kind an imported entry of this compendium type lands in. */
export function importKindFor(type: CompendiumType): 'beast' | 'item' {
  return type === 'monsters' ? 'beast' : 'item';
}

/** Maps a compendium detail to the local entity for its type. */
export function srdToLocal(type: CompendiumType, d: CompendiumDetail): Record<string, unknown> {
  return type === 'monsters' ? srdMonsterToLocal(d) : srdItemToLocal(d);
}
